import { Link, List, mergeStyles, Spinner, Stack } from '@fluentui/react';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { appContext } from '../../../../web/models/getHelp/appContext';
import { ResultType, TopResultData } from '../../../../web/models/selfHelp';
import { BcqrResultsV2 } from '../../../../web/self-help/components/BCQRResultsv2';
import { commonSearchTextCount, GetHelpCommonSearches } from '../../../../web/self-help/components/GetHelpCommonSearches';
import { OsUpdateCard } from '../../../../web/self-help/components/OsUpdateCard';
import { TopResult } from '../../../../web/self-help/components/TopResult';
import { captureContentUpdate } from '../../../../web/utils/oneDsUtil';
import { getBannerText, getIsControl, appendArticleUrl, removeBcqrSolutionTitleSuffix, getBcqrSolutions, getBCQRTitle, getIsDifferentContentType } from '../../../../web/utils/selfHelpUtils';
import { isGetHelpSourceApp } from '../../../../web/utils/statelessUtil';
import { getString } from '../../../../web/utils/stringLoader';
import {
  AlchemySolutionModel,
  AlchemyResult,
  SolutionType,
  BcqrExperimentType,
  ContentType,
  UserInfo,
  AlchemyInsightModel,
} from '../../../models/apiModels';
import { AutomationIds } from '../../../models/constants/automationIds';
import { OutdatedDevicesAlertEnabledComponent, SourceApps } from '../../../models/constants/constants';
import { ISharedState } from '../../../models/reduxState';
import { onOverrideClickBehavior } from '../../../utilities';
import { autoSuggestStateSelector, sharedStateSelector } from '../../app/selectors/contactUsSelector';
import { OutdatedDevicesAlertContainer } from '../containerComponents/OutdatedDevicesAlertContainer';
import { SelfHelpAlchemyInsightsContainer } from '../containerComponents/SelfHelpAlchemyInsightsContainer';
import { SelfHelpIntentGraphContainer } from '../containerComponents/SelfHelpIntentGraphContainer';
import { VsbUpsellAlertContainer } from '../containerComponents/VsbUpsellAlertContainer';
import { AutoSuggestCard } from '../presentationalComponents/AutoSuggestCard';
import { getCommonClassNames, getCommonStyles, getCommonThemedClassNames, getCommonThemedStyles, getLinkStyles } from '../styles/commonStyles';
import { getContactUsClassNames, IContactUsStyles } from '../styles/contactUsStyles';
import { getSelfHelpAlchemyClassNames, getSelfHelpAlchemyStyles } from '../styles/selfHelpAlchemyStyles';
import { IStyledComponent } from '../styles/themesUI';

interface SelfHelpAlchemyProps {
  results: AlchemyResult;
  runningInSoc: boolean;
  isAlchemyQueryInProgress: boolean;
  isAlchmeySolutionsEnabled: boolean;
  isAlchmeyInsightsEnabled: boolean;
  isInsightActionButtonEnabled: boolean;
  isJumpToFirstLinkEnabled: boolean;
  areSolutionsPresent: boolean;
  areInsightsPresent: boolean;
  areAIInsightsPresent: boolean;
  disambiguationBranchingEnabled: boolean;
  showAutoSuggestCard: boolean;
  bcqrExperimentType: BcqrExperimentType;
  sourceApp: SourceApps;
  showRecommendedArticlesHeader: boolean;
  isThirdParty: boolean;
  isTeams: boolean;
  isAgave: boolean;
  isNonAgave: boolean;
  isDarkTheme: boolean;
  market: string;
  isSignedIn: boolean;
  userInfo: UserInfo;
  isTopResultForSelfHelpEnabled: boolean;
  openLinksInNewTab: boolean;
  showGetCurrentBox: boolean;
  showBCQRV2: boolean;
  isGetHelp: boolean;
  articleOnClickHandler: (url: string) => {};
  toggleTopResultHeight: (index: number) => void;
  toggleTopResultContent: (index: number) => void;
}

const commonClassNames = getCommonClassNames(getCommonStyles!, {});

interface BcqrResultProps {
  runningInSoc: boolean;
  alchemyResult: AlchemySolutionModel;
  index: number;
  isThirdParty: boolean;
  bcqrExperimentType: BcqrExperimentType;
  correlationId: string;
  isDifferentContentType: boolean;
  market: string;
  isInApp: boolean;
  isSignedIn: boolean;
  userInfo: UserInfo;
  contentType: ContentType;
  isGetHelp: boolean;
  articleOnClickHandler: (url: string) => {};
}

type IBcqrResultProps = BcqrResultProps & IStyledComponent<IContactUsStyles>;

export const BcqrResult = (props: IBcqrResultProps) => {
  const contactUsClassNames = getContactUsClassNames(props.styles, { theme: props.theme });
  const linkStyles = getLinkStyles();
  const bannerText = getBannerText(props.alchemyResult.ContentType);
  const isControl = getIsControl(props.bcqrExperimentType);
  const userEmail = !!props.userInfo ? props.userInfo.MemberName : null;
  const articleUrl = appendArticleUrl(props.alchemyResult.URI, props.correlationId, isControl, props.isSignedIn, userEmail, props.contentType, props.market, props.isGetHelp);
  const shouldShowBanners = !isControl && props.isDifferentContentType;
  if (props.runningInSoc || props.isThirdParty || props.isInApp) {
    return (
      <div className={mergeStyles('col-lg-12', contactUsClassNames.bodyCopySmall, commonClassNames.ellipsisText, contactUsClassNames.col)}>
        <Link
          id={props.alchemyResult.Source + props.index}
          className={contactUsClassNames.articleLink}
          styles={linkStyles}
          data-bi-bhvr="SHOW"
          data-bi-area="selfHelpAlchemyBCQR"
          data-bi-contentType={props.alchemyResult.ContentType}
          data-bi-formnm="maxFunnel"
          data-bi-name={props.alchemyResult.Title}
          data-bi-srchlinkpos={props.index}
          data-bi-iscontactsuggestedselfhelp="true"
          target="_blank"
          href={articleUrl}
        >
          {!isControl ? removeBcqrSolutionTitleSuffix(props.alchemyResult.Title, props.market) : props.alchemyResult.Title}
        </Link>
        <div>
          {
            !!shouldShowBanners && (<span className={contactUsClassNames.banner}>{bannerText}</span>)
          }
        </div>
        <p className={mergeStyles(contactUsClassNames.articleContentShortened, commonClassNames.overflowHidden, commonClassNames.fontSizeInherit)}>
          <div data-automation-id={AutomationIds.SolutionsDescription} className={contactUsClassNames.searchSnippet}>{props.alchemyResult.Description}</div>
        </p>
      </div>
    );
  } else {
    return (
      <div className={mergeStyles('col-lg-12', contactUsClassNames.bodyCopySmall, commonClassNames.ellipsisText, contactUsClassNames.col)}>
        <Link
          id={props.alchemyResult.Source + props.index}
          className={contactUsClassNames.articleLink}
          styles={linkStyles}
          data-bi-bhvr="SHOW"
          data-bi-area="selfHelpAlchemyBCQR"
          data-bi-formnm="maxFunnel"
          data-bi-name={props.alchemyResult.Title}
          data-bi-srchlinkpos={props.index}
          data-bi-iscontactsuggestedselfhelp="true"
          href="javascript:void(0)"
          onClick={() => props.articleOnClickHandler(articleUrl)}
        >
          {' '}
          {props.alchemyResult.Title}{' '}
        </Link>
        <div>
          {
            !!shouldShowBanners && (<span className={mergeStyles(contactUsClassNames.banner, commonClassNames.ellipsisText)}>{bannerText}</span>)
          }
        </div>
        <p className={mergeStyles(contactUsClassNames.articleContentShortened, commonClassNames.overflowHidden, commonClassNames.fontSizeInherit)}>
          <div className={contactUsClassNames.searchSnippet}>{props.alchemyResult.Description}</div>
        </p>
      </div>
    );
  }
};

interface BcqrResultsProps {
  results: Array<AlchemySolutionModel>;
  runningInSoc: boolean;
  showRecommendedArticlesHeader: boolean;
  isThirdParty: boolean;
  isGetHelp: boolean;
  sourceApp: SourceApps;
  bcqrExperimentType: BcqrExperimentType;
  correlationId: string;
  market: string;
  isInApp: boolean;
  isDarkTheme: boolean;
  isSignedIn: boolean;
  userInfo: UserInfo;
  articleOnClickHandler: (url: string) => {};
}

type IBcqrResultsProps = BcqrResultsProps & IStyledComponent<IContactUsStyles>;

const BcqrResults = (props: IBcqrResultsProps) => {
  let index = 1;
  const solutions = getBcqrSolutions(props.bcqrExperimentType, props.results);
  const commonThemedClassNames = getCommonThemedClassNames(getCommonThemedStyles, { theme: props.theme, isDarkTheme: props.isDarkTheme });
  const selfHelpAlchemyClassNames = getSelfHelpAlchemyClassNames(getSelfHelpAlchemyStyles);
  const contactUsClassNames = getContactUsClassNames(props.styles, { theme: props.theme });
  const bcqrSolutionsTitle = getBCQRTitle();
  const isDifferentContentType = getIsDifferentContentType(solutions);
  const results = solutions.map((result) => (
    <BcqrResult
      key={result.Title}
      alchemyResult={result}
      index={index++}
      runningInSoc={props.runningInSoc}
      isThirdParty={props.isThirdParty}
      isInApp={props.isInApp}
      theme={props.theme}
      styles={props.styles}
      articleOnClickHandler={props.articleOnClickHandler}
      bcqrExperimentType={props.bcqrExperimentType}
      correlationId={props.correlationId}
      isDifferentContentType={isDifferentContentType}
      market={props.market}
      isSignedIn={props.isSignedIn}
      userInfo={props.userInfo}
      contentType={result.ContentType}
      isGetHelp={props.isGetHelp}
    />
  ));

  return (
    <div data-automation-id={AutomationIds.BCQRSolutionsTitle}
      className={mergeStyles(contactUsClassNames.row, selfHelpAlchemyClassNames.articleDiv)}>
      {props.showRecommendedArticlesHeader && (
        <div className={props.sourceApp === SourceApps.SurfaceApp || props.sourceApp === SourceApps.SurfaceApp2 ? commonThemedClassNames.h2 : commonThemedClassNames.header}>
          {bcqrSolutionsTitle}
        </div>
      )}
      {results}
    </div>
  );
};

interface NoResultsProps {
  noResultText: string;
  ariaLabel: string;
}

type INoResultsProps = NoResultsProps & IStyledComponent<IContactUsStyles>;

const NoResults = (props: INoResultsProps) => {
  const contactUsClassNames = getContactUsClassNames(props.styles, { theme: props.theme });
  return (
    <div id="noSelfHelpAlchemyResults" className={mergeStyles(contactUsClassNames.row, contactUsClassNames.noGutter)}>
      <div className={mergeStyles('col-lg-12', contactUsClassNames.bodyCopySmall, commonClassNames.ellipsisText, contactUsClassNames.col)}
           aria-label={props.ariaLabel}
           aria-relevant='additions text'
           aria-atomic={false}
           aria-live='polite'>{props.noResultText}</div>
    </div>
  );
};

interface GettingResultsProps {
  gettingResultsText: string;
  ariaLabel: string;
}

type IGettingResultsProps = GettingResultsProps & IStyledComponent<IContactUsStyles>

const GettingResults: React.FC<IGettingResultsProps> = (props) => {
  const contactUsClassNames = getContactUsClassNames(props.styles, { theme: props.theme });
  return (
    <div id="gettingSelfHelpAlchemyResults"
         className={mergeStyles(contactUsClassNames.row, contactUsClassNames.noGutter)}>
      <div className={mergeStyles('col-lg-12', contactUsClassNames.bodyCopySmall, commonClassNames.ellipsisText, contactUsClassNames.col)}>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
            <Spinner styles={{ circle: { color: props.theme.palette.accent } }}
                     ariaLive='assertive'
                     ariaLabel={props.ariaLabel} />
          <span>{props.gettingResultsText + '...'}</span>
        </Stack>
      </div>
    </div>
  );
};

const searchSuggestionsString = getString('SearchSuggestions');

type ISelfHelpAlchemyProps = SelfHelpAlchemyProps & IStyledComponent<IContactUsStyles>;

const SelfHelpAlchemyBody = (
  props: ISelfHelpAlchemyProps,
  showBcqrResults: boolean,
  solutionsData: AlchemySolutionModel[],
  autoSuggestionCount: number
) => {
  const selfHelpAlchemyClassNames = getSelfHelpAlchemyClassNames(getSelfHelpAlchemyStyles, { theme: props.theme });
  const contactUsClassNames = getContactUsClassNames(props.styles, { theme: props.theme });
  const correlationId = (!!props.results && props.results.CorrelationId) || '';
  const isBranching =
    !!props.disambiguationBranchingEnabled &&
    !!props.results &&
    !!props.results.Insights &&
    props.results.Insights.length > 0 &&
    props.results.Insights[0].SolutionType &&
    props.results.Insights[0].SolutionType.toLowerCase() ===
    SolutionType.Branching.toLowerCase();
  const showAlchemyInsightComponent =
    !isBranching &&
    props.isAlchmeyInsightsEnabled &&
    props.areInsightsPresent;

  const resultsCount =
      // In the gethelp app, in absence of results
      (!props.results && isGetHelpSourceApp(props.sourceApp) && appContext.locale.startsWith('en')
        ? commonSearchTextCount
        : 0) +
      // Top results hide insights, but not branching
      (props.isTopResultForSelfHelpEnabled && !!props.results?.TopResults?.length
        ? props.results.TopResults.length + (isBranching ? 1 : 0)
        : (isBranching || showAlchemyInsightComponent ? 1 : 0)) +
      getBcqrSolutions(props.bcqrExperimentType, solutionsData).length +
      (props.showAutoSuggestCard ? autoSuggestionCount : 0);

  const intentGraphComponent = isBranching ? (
    <SelfHelpIntentGraphContainer
      data-automation-id={AutomationIds.IntentGraph}
      graphInsights={props.results.Insights}
      areaName={'selfHelpAlchemyBranching'}
      key={correlationId}
      correlationId={correlationId}
    />
  ) : null;

  const insightsComponent = showAlchemyInsightComponent ? (
    <div
      key={`insightsComponent-${correlationId}`}
      className={
        props.runningInSoc || !props.isInsightActionButtonEnabled || props.isThirdParty
          ? selfHelpAlchemyClassNames.socView
          : ''
      }
    >
      <SelfHelpAlchemyInsightsContainer
        data-automation-id={AutomationIds.Insights}
        insights={props.results.Insights}
      />
    </div>
  ) : null;

  let bcqrResultsComponent = null;
  if (showBcqrResults) {
    if (props.showBCQRV2) {
      bcqrResultsComponent = (<div data-automation-id={AutomationIds.RecommendedArticleBranch} key={`BcqrResultsContainer-${correlationId}`} className={contactUsClassNames.panelBody}>
        <BcqrResultsV2
          key={`BcqrResultsV2-${props.results.CorrelationId}`}
          data-automation-id={AutomationIds.Solutions}
          results={solutionsData}
          runningInSoc={props.runningInSoc}
          isThirdParty={props.isThirdParty}
          articleOnClickHandler={props.articleOnClickHandler}
          theme={props.theme}
          styles={props.styles}
          showRecommendedArticlesHeader={props.showRecommendedArticlesHeader}
          sourceApp={props.sourceApp}
          bcqrExperimentType={props.bcqrExperimentType}
          correlationId={props.results.CorrelationId}
          market={props.market}
          isInApp={props.isAgave || props.isNonAgave}
          isDarkTheme={props.isDarkTheme}
          isSignedIn={props.isSignedIn}
          userInfo={props.userInfo}
          isGetHelp={props.isGetHelp}
        />
      </div>);
    } else {
      bcqrResultsComponent = (<div key={`BcqrResultsContainer-${correlationId}`} className={contactUsClassNames.panelBody}>
        <BcqrResults
          key={`BcqrResults-${props.results.CorrelationId}`}
          data-automation-id={AutomationIds.Solutions}
          results={solutionsData}
          runningInSoc={props.runningInSoc}
          isThirdParty={props.isThirdParty}
          articleOnClickHandler={props.articleOnClickHandler}
          theme={props.theme}
          styles={props.styles}
          showRecommendedArticlesHeader={props.showRecommendedArticlesHeader}
          sourceApp={props.sourceApp}
          bcqrExperimentType={props.bcqrExperimentType}
          correlationId={props.results.CorrelationId}
          market={props.market}
          isInApp={props.isAgave || props.isNonAgave}
          isDarkTheme={props.isDarkTheme}
          isSignedIn={props.isSignedIn}
          userInfo={props.userInfo}
          isGetHelp={props.isGetHelp}
        />
      </div>);
    }
  }

  const inAppArticleHandler = (event?: React.MouseEvent) => {
    if (props.isGetHelp && !props.isDarkTheme) {
      onOverrideClickBehavior(props.articleOnClickHandler)(event);
    }
  };

  const autoSuggestCard = props.showAutoSuggestCard && <AutoSuggestCard key={`AutoSuggestCardContainer-${correlationId}`} />;
  let components: React.ReactNode[] = [];
  let body = null;
  if (props.isTopResultForSelfHelpEnabled) {
    // Todo: Brook - refactor and move to another file
    const showChevronIcon = props.results.TopResults && props.results.TopResults.length > 1;

    const onRenderCell = (topResult?: TopResultData, index?: number) => {
      if (!!topResult && topResult.type !== ResultType.MultiTurn) {
        return (
          <TopResult
            key={`${topResult.title}-${index}`}
            title={topResult.title}
            description={topResult.content}
            type={topResult.type}
            url={topResult.url}
            contentType={topResult.contentType}
            expanded={topResult.expanded}
            onClickExpand={(index: number) => {
              props.toggleTopResultHeight(index);
            }}
            insights={props.results.Insights}
            topResult={topResult}
            index={index}
            showChevronIcon={showChevronIcon}
            toggleTopResultContent={props.toggleTopResultContent}
            openLinksInNewTab={props.openLinksInNewTab}
          />
        );
      }
      return null;
    };

    const topResults = props.results && props.results.TopResults;
    let topResultComponent = null;
    if (!!topResults && topResults.length > 0) {
      const topResultsType = topResults[0].type;
      if (topResultsType === ResultType.Insight) {
        // handle insights
        topResultComponent = <List key={`topresults-list-${correlationId}`} items={topResults} onRenderCell={onRenderCell} />;
      } else if (topResultsType === ResultType.AlchemyGPT) {
        // handle AlchemyGpt
        const topResult = topResults[0];
        topResultComponent = (<TopResult
          key={`${topResult.title}-${0}`}
          title={topResult.title}
          description={topResult.content}
          type={topResult.type}
          url={topResult.url}
          contentType={topResult.contentType}
          expanded={topResult.expanded}
          onClickExpand={(index: number) => {
            props.toggleTopResultHeight(index);
          }}
          topResult={topResult}
          index={0}
          showChevronIcon={showChevronIcon}
          toggleTopResultContent={props.toggleTopResultContent}
          openLinksInNewTab={props.openLinksInNewTab}
        />);
      } else {
        // handle BCQRs
        const topResult = topResults[0];
        topResultComponent = (<TopResult
          key={`${topResult.title}-${0}`}
          title={topResult.title}
          description={topResult.content}
          type={topResult.type}
          url={topResult.url}
          contentType={topResult.contentType}
          expanded={topResult.expanded}
          onClickExpand={(index: number) => {
            props.toggleTopResultHeight(index);
          }}
          topResult={topResult}
          index={0}
          showChevronIcon={showChevronIcon}
          toggleTopResultContent={props.toggleTopResultContent}
          openLinksInNewTab={props.openLinksInNewTab}
        />);
      }
    }
    components = [intentGraphComponent, topResultComponent, bcqrResultsComponent, autoSuggestCard];
    body = <div onClick={inAppArticleHandler}>
              <div aria-label={searchSuggestionsString.replace('{0}', resultsCount.toString())}
                    aria-relevant='additions text'
                    aria-atomic={false}
                    aria-live='assertive'></div>
              {components}</div>;
  } else {
    components = [intentGraphComponent, insightsComponent, bcqrResultsComponent, autoSuggestCard];
    body = <><div aria-label={searchSuggestionsString.replace('{0}', resultsCount.toString())}
                aria-relevant='additions text'
                aria-atomic={false}
                aria-live='assertive'></div>
            {components}</>;
  }

  return body;
};

export const SelfHelpAlchemy: React.FC<ISelfHelpAlchemyProps> = (
  props
) => {
  const prevAreSolutionsPresent = React.useRef(false);
  const prevAreInsightsPresent = React.useRef(false);
  const prevAreAIInsightsPresent = React.useRef(false);
  React.useEffect(() => {
    logInsightAndSolutionLoad();
  }, [props.areInsightsPresent, props.areSolutionsPresent, props.areAIInsightsPresent]);

  // Activate the first interactive component.
  // There's a trick to this: subcomponents might take longer to render than the parent,
  // so we need to keep checking for the first interactive child. This is simpler
  // (and a lot less spaghetti'd) than cross-component communication.
  const containerRef = React.useRef<HTMLDivElement>(null);
  const firstInteractiveChildRef = React.useRef<HTMLElement>(null);
  if (props.isJumpToFirstLinkEnabled) {
  React.useEffect(() => {
    if (!!containerRef?.current && !!firstInteractiveChildRef)
    {
      const interval = setInterval(() => {
        const firstInteractiveChild = containerRef.current.querySelector<HTMLElement>('a, button');
        if (firstInteractiveChildRef.current != firstInteractiveChild)
        {
          firstInteractiveChildRef.current = firstInteractiveChild;
          firstInteractiveChild?.focus();
        }
      }, 250); // We don't need a high polling frequency
      return (() => clearInterval(interval)); // Stop polling before next render
    }
  });
}

  // Obtain 'Related Searches' and count them.
  const autoSuggestState = useSelector(autoSuggestStateSelector);
  const queries = autoSuggestState.autoSuggestCardResultsV2?.suggestions || [];
  const contactSearchQuery = (useSelector(sharedStateSelector) as ISharedState)
    ?.contactSearchedQuery
    .toLowerCase() ||
    '';
  const autoSuggestionCount =
    autoSuggestState.isAutoSuggestEnabled &&
    queries.filter(query => query.toLowerCase() !== contactSearchQuery).length ||
    0;

  const logInsightAndSolutionLoad = () => {
    if (
      props.isAlchmeySolutionsEnabled &&
      !prevAreSolutionsPresent.current &&
      props.areSolutionsPresent
    ) {
      const bcqrs: AlchemySolutionModel[] = props.results.Solutions.map((bcqr: AlchemySolutionModel) => {
        const copiedBCQR: any = { ...bcqr };
        // AlchemySolutionModel.Description could be long, although it usually does not cause logging issue.
        // Remove AlchemySolutionModel.Description and only log the length to reduce the logging size.
        copiedBCQR.DescriptionLength = !!copiedBCQR.Description ? copiedBCQR.Description.length : 0;
        delete copiedBCQR.Description;
        return copiedBCQR;
      });
      const bcqrOverrideTags = {
        content: {
          contentName: 'alchemySolutionResults',
          aimId: props.results.CorrelationId,
          theme: props.results.Theme,
          formnm: 'maxFunnel',
          selfHelpResults: bcqrs,
          areaName: 'selfHelpAlchemyBCQR',
        },
        behavior: 160,
      };

      captureContentUpdate(bcqrOverrideTags);
    }

    if (
      props.isAlchmeyInsightsEnabled &&
      !prevAreInsightsPresent.current &&
      props.areInsightsPresent
    ) {
      const insights: AlchemyInsightModel[] = props.results.Insights.map((insight: AlchemyInsightModel) => {
        const copiedInsight: any = { ...insight };
        // AlchemyInsightModel.Description could be very long for branching and cause the JSLL/1DS logging fail.
        // Remove it from the logging, and only log the length.
        copiedInsight.DescriptionLength = !!copiedInsight.Description ? copiedInsight.Description.length : 0;
        delete copiedInsight.Description;
        return copiedInsight;
      });

      const insightOverrideTags = {
        content: {
          contentName: 'alchemyInsightResults',
          aimId: props.results.CorrelationId,
          theme: props.results.Theme,
          formnm: 'maxFunnel',
          selfHelpResults: insights,
          areaName: 'selfHelpAlchemyInsight',
        },
        behavior: 160,
      };

      captureContentUpdate(insightOverrideTags);
    }

    if (
      props.isAlchmeyInsightsEnabled &&
      !prevAreAIInsightsPresent.current &&
      props.areAIInsightsPresent
    ){
      const aiinsightOverrideTags = {
        content: {
          contentName: 'alchemyAIInsightResults',
          aimId: props.results.CorrelationId,
          theme: props.results.Theme,
          aiinsightLength: props.results.AIInsights?.GPTInsight?.length,
          formnm: 'maxFunnel',
          areaName: 'selfHelpAlchemyAIInsight',
        },
        behavior: 160,
      }
      captureContentUpdate(aiinsightOverrideTags);
    }

    prevAreSolutionsPresent.current = props.areSolutionsPresent;
    prevAreInsightsPresent.current = props.areInsightsPresent;
  };

  const contactUsClassNames = getContactUsClassNames(props.styles, { theme: props.theme });
  if (!props.isAlchmeySolutionsEnabled && !props.isAlchmeyInsightsEnabled) {
    return null;
  }

  let outdatedDevicesAlert = null;
  if (isGetHelpSourceApp(props.sourceApp)) {
    outdatedDevicesAlert = props.showGetCurrentBox ? <OsUpdateCard correlationId={props.results.CorrelationId} /> : null;
  } else {
    outdatedDevicesAlert = (
      <OutdatedDevicesAlertContainer callingComponent={OutdatedDevicesAlertEnabledComponent.SelfHelpAlchemy} />
    );
  }
  const vsbUpsellAlert = (
    <VsbUpsellAlertContainer
      key={'VsbUpsellAlertContainer'}
      callingComponent={OutdatedDevicesAlertEnabledComponent.SelfHelpAlchemy} />
  );

  const solutionsData = !!props.results
    ? !!props.results.Solutions
      ? props.results.Solutions
      : []
    : null;
  const showBcqrResults = !!solutionsData && solutionsData.length > 0 && !!solutionsData[0];
  const hasInsights = !!props.results && !!props.results.Insights && props.results.Insights.length > 0;

  let body;
  if ((!props.results && !props.isAlchemyQueryInProgress)) {
    body = null;
  } else if (props.isAlchemyQueryInProgress) {
    body = <div className={contactUsClassNames.panelBody}>
      <GettingResults gettingResultsText={getString('result_gettingSelfHelpText')}
                      ariaLabel={getString('result_gettingSelfHelpText')}
                      theme={props.theme}
                      styles={props.styles} />
    </div>;
  } else if (!showBcqrResults && !hasInsights) {
    body = <div className={contactUsClassNames.panelBody}>
      <NoResults noResultText={getString('result_contactUsNoResults')}
                  ariaLabel={getString('result_contactUsNoResults')}
                  theme={props.theme}
                  styles={props.styles} />
    </div>;
  } else {
    body = SelfHelpAlchemyBody(props, showBcqrResults, solutionsData, autoSuggestionCount);
  }

  const gethelpCommonSearches: JSX.Element =
    (!props.results &&
      !props.isAlchemyQueryInProgress &&
      isGetHelpSourceApp(props.sourceApp) &&
      appContext.locale.startsWith('en')) ? <GetHelpCommonSearches /> : null;

  return (
    <div data-automation-id={AutomationIds.AutoSuggestNodeBranch} className={mergeStyles(contactUsClassNames.row, contactUsClassNames.noGutter)}>
      <div className={mergeStyles('col-lg-10', contactUsClassNames.col, contactUsClassNames.articleSection)}>
        <div className={contactUsClassNames.panelGroup}>
          <div aria-live="polite" className={mergeStyles(contactUsClassNames.panel, 'panel-default')} ref={containerRef}>
            {outdatedDevicesAlert}
            {vsbUpsellAlert}
            {body}
            {gethelpCommonSearches}
          </div>
        </div>
      </div>
    </div>
  );
};
